import { useEffect, useState, type ReactNode } from "react";
import { Sidebar } from "./Sidebar.js";
import { Topbar } from "./Topbar.js";
import { ChatPanel } from "../components/chat/ChatPanel.js";
import { CommandPalette } from "../components/CommandPalette.js";
import { useRoute, type Route } from "../lib/useRoute.js";

/**
 * Top-level application shell. Owns the route state, the sidebar, the topbar,
 * and the chat panel. Every page receives its current route as a hint so
 * page-aware features (chat context, breadcrumbs) work without prop drilling.
 */
export function AppShell({ render }: { render: (route: Route) => ReactNode }) {
  const [route, setRoute] = useRoute();
  const [chatOpen, setChatOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // ⌘K / Ctrl+K opens the command palette; ⌘J / Ctrl+J opens the AI chat.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setChatOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex min-h-screen bg-canvas">
      <Sidebar route={route} onRoute={setRoute} onOpenChat={() => setChatOpen(true)} />

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          route={route}
          onOpenChat={() => setChatOpen(true)}
          onOpenPalette={() => setPaletteOpen(true)}
          onNavigate={setRoute}
        />
        <main className="flex-1 p-6 lg:p-8">
          <div className="mx-auto max-w-7xl animate-fade-in">{render(route)}</div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} onNavigate={setRoute} />
      {chatOpen && <ChatPanel route={route} onClose={() => setChatOpen(false)} />}
    </div>
  );
}
